import { DateRange } from '@angular/material/datepicker';
import {
    endOfDay,
    formatISO,
    Interval,
    parse as _parse,
    set,
    startOfDay,
    startOfToday,
    startOfYesterday,
    subDays,
    toDate as _toDate,
    eachMinuteOfInterval,
    addMinutes,
    subHours,
    addHours,
    isWithinInterval,
    isSameDay,
    isAfter,
} from 'date-fns';
import { DateRangeInterval } from '../models';
import { getDaylightSavingSwitchDates } from './date-columns.helper';

/**
 * generates empty array from range
 * @param length
 */
function range(length: number) {
    return Array.from({ length });
}

/**
 * Array of DateRange intervals
 * ```
 * [
 * ...
 * {
 * "start": "2023-03-02T09:00:00.000Z",
 * "end": "2023-03-02T09:30:00.000Z"
 * },
 * {
 * "start": "2023-03-02T09:30:00.000Z",
 * "end": "2023-03-02T10:00:00.000Z"
 * }
 * ...
 * ]
 * ```
 * @param day
 */
export function generate30MinutesRangesFor1Day(day: Date = new Date()): DateRange<Date>[] {
    const slicedInterval = eachMinuteOfInterval({ start: startOfDay(day), end: endOfDay(day) }, { step: 30 });
    return slicedInterval.map((intervalDateItem, index) => {
        const end = index === slicedInterval.length - 1 ? endOfDay(day) : addMinutes(intervalDateItem, 30);
        return new DateRange(intervalDateItem, end);
    });
}

/**
 * formats number to 2v string
 * @param n
 * @returns string
 */
export function paddToTwoSymbols(n: number): string {
    if (n < 10) {
        return '0' + n;
    }

    return n.toString();
}

/**
 * Return the formatted date string in ISO 8601 format. Options may be passed to control the parts and notations of the date.
 * @param format
 */
export function getISOString(format: 'extended' | 'basic' = 'basic'): string {
    return formatISO(new Date(), { format });
}

/**
 * Get Start Of Today
 *
 */
export const getStartOfToday = (): Date => {
    return startOfToday();
};

/**
 * Get Start Of Yesterday
 *
 */
export const getStartOfYesterday = (): Date => {
    return startOfYesterday();
};

/**
 * Get Start Of Day Days Ago
 * @param date
 * @param subDaysCount
 */
export const getStartOfDaysAgo = (date: Date, subDaysCount = 0): Date => {
    return startOfDay(subDays(date, subDaysCount));
};

/**
 * Is valid date range
 * @param range
 */
export function isValidDateRange(range: DateRangeInterval): boolean {
    if (!range) {
        return false;
    }

    const start = toDate(range.start);
    const end = toDate(range.end);

    return !isNaN(start.getTime()) && !isNaN(end.getTime()) && !(start > end);
}

/**
 * Conver to valid Date object
 * @param date
 */
export function toDate(date: Date | number | string): Date {
    if (!date) {
        return;
    }

    if (typeof date === 'string') {
        return new Date(date);
    }
    return _toDate(date as Date | number);
}

/**
 * Return the date parsed from string using the given format string.
 *
 * `parse('2019-01-01', 'yyyy-MM-dd', new Date());`
 * @param dateString
 * @param formatString
 * @param referenceDate
 * @param options
 */
export function parseDate(
    dateString: string,
    formatString: string,
    referenceDate = new Date(),
    options?: any,
): Date | null {
    // if parsing failed, `Invalid Date` will be returned. let's catch RangeError and return null instead
    try {
        return _parse(dateString, formatString, referenceDate, options);
    } catch (error) {
        return null;
    }
}

/**
 * Converts an array of UTC times to local time with respect to DST shifts.
 * @param {string[]} utcTimes - An array of UTC times.
 * @param {string} createdAt - date of adding times to track DST
 * @param isCreating
 * @returns {string[]} - The converted array of local times.
 */
export function convertUTCHoursToLocalTime(utcTimes: string[], createdAt: string, isCreating: boolean): string[] {
    return utcTimes?.slice()?.map(t => {
        let dstMissingHourOffset = 0;
        let utcHour = parseInt(t.split(':')[0], 10); // Extract the hour part and convert it to a number
        const now = new Date();
        const utcHourDate = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), utcHour, 0, 0));
        utcHourDate.setUTCHours(utcHour, 0, 0);

        const isStartInDst = isDSTinEffect(new Date(createdAt));
        const isHourInDst = isDSTinEffect(utcHourDate);
        // if today is the last Sunday of March
        const missingHourInterval = getDstMissingHourInterval(new Date());
        const dstStartToday = isSameDay(new Date(), missingHourInterval.start);
        // if startDate or frequency hour and now have different offset
        if (createdAt && isHourInDst !== isStartInDst) {
            let originalDateHour = new Date(utcHourDate);
            // TODO: replace new Date with selectedViewDate

            // if the timestamp falls into last Sunday of March 1:00-2:00 UTC
            if (isWithinInterval(originalDateHour, missingHourInterval)) {
                dstMissingHourOffset = 1;
            }

            // // if today is the last Sunday of March and medication is being created
            if (dstStartToday && isSameDay(new Date(createdAt), missingHourInterval.start)) {
                if (isAfter(originalDateHour, missingHourInterval.end)) {
                    originalDateHour = addHours(originalDateHour, 1);
                }
            }

            originalDateHour = isStartInDst ? addHours(originalDateHour, 1) : subHours(originalDateHour, 1);

            utcHour = new Date(originalDateHour).getUTCHours();
        }
        const utcDate = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), utcHour, 0, 0));
        const localHour = new Date(utcDate).getHours() + dstMissingHourOffset;
        return localHour.toString().padStart(2, '0') + ':00:00';
    });
}

/**
 * Gets the interval during which the DST missing hour occurs.
 *
 * Daylight Saving Time (DST) starts on the last Sunday of March at 1:00 AM UTC.
 * This function calculates the interval for the DST missing hour for given year.
 * @param date - The reference date to determine the year for DST calculation.
 * @returns An interval representing the DST missing hour.
 */
export function getDstMissingHourInterval(date: Date): Interval {
    // DST starts on the last Sunday of March at 1:00 AM UTC
    const year = date.getFullYear();
    const marchLastSunday = getDaylightSavingSwitchDates(year).start;
    const start = new Date(marchLastSunday);
    const end = new Date(marchLastSunday);

    const timezoneOffset = start.getTimezoneOffset() / 60;
    start.setHours(1 - timezoneOffset, 0, 0, 0);
    end.setHours(start.getHours() + 1);
    return { start, end };
}

/**
 Converts a UTC hour number to a local time hour number.
 * @param utcHour - The UTC hour number to be converted.
 * @returns The local time hour number.
 */
export function convertUTCHourToLocalHour(utcHour: number): number {
    const utcDate = new Date().setUTCHours(utcHour);
    const localHour = new Date(utcDate).getHours();
    return localHour;
}

/**
 Converts a UTC hour number to a local time hour number.
 * @param time
 * @returns The local time hour number.
 */
export function convertLocalHourToUTCHour(time: string): string {
    const hour = parseInt(time.split(':')[0], 10); // Extract the hour part and convert it to a number
    const date = new Date().setHours(hour, 0, 0, 0);
    const utcHour = new Date(date).getUTCHours();
    return utcHour.toString().padStart(2, '0') + ':00';
}

/**
 * Converts an array of UTC times to local time.
 * @param times - An array of local times.
 * @returns {string[]} - The converted array of UTC times.
 */
export function convertLocalHoursToUTC(times: string[]): string[] {
    return times?.slice()?.map(t => set(new Date(), { hours: +t.split(':')[0] }).getUTCHours() + ':00:00');
}

/**
 * Checks if DST is in effect on the given date.
 *
 * This function calculates the timezone offset on January 1st and July 1st of the given year.
 * If the maximum of these two values is not equal to the timezone offset of the given date,
 * then DST is in effect.
 * @param date - The date to check.
 * @returns True if DST is in effect, false otherwise.
 */
export function isDSTinEffect(date: Date) {
    const jan = new Date(date.getFullYear(), 0, 1).getTimezoneOffset();
    const jul = new Date(date.getFullYear(), 6, 1).getTimezoneOffset();
    return Math.max(jan, jul) !== date.getTimezoneOffset();
}

/**
 * Adjusts the frequency times of a medication or procedure prescription to account for DST changes.
 *
 * This function modifies the `frequencyTimes` of the given payload by adjusting the UTC hours
 * based on whether the start date and the current date are in different DST states. If the DST
 * state differs, the function adjusts the hours accordingly to maintain the correct local time.
 * @param times
 * @param startDate
 */
export function patchCustomFrequencyTimes(times: string[], startDate: Date): string[] {
    // patch utc hours based on dst offset
    const isDstNow = isDSTinEffect(new Date());
    const isDstStartDate = isDSTinEffect(startDate);
    const adjustedTimes = times.map(t => {
        let originalDateHour = new Date();
        originalDateHour.setUTCHours(Number(t.slice(0, 2)));
        // if different offsets
        if (isDstNow !== isDstStartDate) {
            originalDateHour = isDstStartDate ? subHours(originalDateHour, 1) : addHours(originalDateHour, 1);
        }
        const adjustedHour = new Date(originalDateHour).getUTCHours();
        return `${paddToTwoSymbols(adjustedHour)}:00:00`;
    });

    return adjustedTimes;
}
